import { alignmentTypes } from '../../../common/constants/alignment-types';
import axis from '../constants/axis';

// Pre-computed alignment types to avoid runtime string concatenation
const X_ALIGNMENT_TYPES = [
  alignmentTypes.LEFT_LEFT,
  alignmentTypes.RIGHT_RIGHT,
  alignmentTypes.LEFT_RIGHT,
  alignmentTypes.RIGHT_LEFT,
  alignmentTypes.CENTER_CENTER,
  alignmentTypes.LEFT_CENTER,
  alignmentTypes.RIGHT_CENTER,
  alignmentTypes.CENTER_LEFT,
  alignmentTypes.CENTER_RIGHT,
];

const Y_ALIGNMENT_TYPES = [
  alignmentTypes.TOP_TOP,
  alignmentTypes.BOTTOM_BOTTOM,
  alignmentTypes.TOP_BOTTOM,
  alignmentTypes.BOTTOM_TOP,
  alignmentTypes.CENTER_CENTER,
  alignmentTypes.TOP_CENTER,
  alignmentTypes.BOTTOM_CENTER,
  alignmentTypes.CENTER_TOP,
  alignmentTypes.CENTER_BOTTOM,
];

function calculateCorrection({
  initialElementBounds,
  positioningAreaBounds,
  targetElementBounds,
  distance,
  element,
  correctionDistance = 5,
  lockedAxis = null, // 'x' | 'y' | null
}) {
  const [distanceX, distanceY] = distance;

  // Helper function to calculate corrections for an axis (optimized)
  const calculateAxisCorrections = (offsets, threshold, axisName, elementBounds) => {
    const alignmentTypesArray = axisName === axis.X ? X_ALIGNMENT_TYPES : Y_ALIGNMENT_TYPES;

    const result = offsets.reduce(
      (acc, value, index) => {
        // Skip zero values early
        if (value === 0) {
          return acc;
        }

        const abs = Math.abs(value);

        // Only process if it's within threshold and smaller than current smallest
        if (abs < threshold && abs < acc.smallestAbs) {
          return {
            smallestCorrection: value,
            smallestAbs: abs,
            alignmentType: alignmentTypesArray[index],
          };
        }

        return acc;
      },
      {
        smallestCorrection: null,
        smallestAbs: threshold,
        alignmentType: null,
      }
    );

    return {
      correction: result.smallestCorrection || 0,
      meta:
        result.smallestCorrection !== null
          ? {
              axis: axisName,
              alignmentType: result.alignmentType,
              elementBounds,
            }
          : null,
    };
  };

  // Pre-calculate bounds values to avoid repeated property access
  const {
    left: targetLeft,
    right: targetRight,
    top: targetTop,
    bottom: targetBottom,
  } = targetElementBounds;
  const {
    left: initialLeft,
    right: initialRight,
    top: initialTop,
    bottom: initialBottom,
  } = initialElementBounds;

  // Calculate center points
  const targetCenterX = targetLeft + (targetRight - targetLeft) / 2;
  const targetCenterY = targetTop + (targetBottom - targetTop) / 2;
  const initialCenterX = initialLeft + (initialRight - initialLeft) / 2;
  const initialCenterY = initialTop + (initialBottom - initialTop) / 2;

  // Calculate X-axis offsets (simplified - just values, no objects)
  const xOffsets = [
    // Same-side alignments (left-left, right-right)
    targetLeft - (initialLeft + distanceX),
    targetRight - (initialRight + distanceX),
    // Opposite-side alignments (left-right, right-left)
    targetRight - (initialLeft + distanceX),
    targetLeft - (initialRight + distanceX),
    // Center alignments
    targetCenterX - (initialCenterX + distanceX), // center-center
    targetCenterX - (initialLeft + distanceX), // left-center
    targetCenterX - (initialRight + distanceX), // right-center
    targetLeft - (initialCenterX + distanceX), // center-left
    targetRight - (initialCenterX + distanceX), // center-right
  ];

  // Calculate Y-axis offsets (simplified - just values, no objects)
  const yOffsets = [
    // Same-side alignments (top-top, bottom-bottom)
    targetTop - (initialTop + distanceY),
    targetBottom - (initialBottom + distanceY),
    // Opposite-side alignments (top-bottom, bottom-top)
    targetBottom - (initialTop + distanceY),
    targetTop - (initialBottom + distanceY),
    // Center alignments
    targetCenterY - (initialCenterY + distanceY), // center-center
    targetCenterY - (initialTop + distanceY), // top-center
    targetCenterY - (initialBottom + distanceY), // bottom-center
    targetTop - (initialCenterY + distanceY), // center-top
    targetBottom - (initialCenterY + distanceY), // center-bottom
  ];

  let xResult = null;
  let yResult = null;

  // We need this check to not calculate corrections if the element is going to be outside
  // the positioning area
  // Respect lockedAxis lock: skip computing correction on the other axis
  if (lockedAxis !== axis.Y) {
    if (
      initialElementBounds.right + distanceX <= positioningAreaBounds.right &&
      initialElementBounds.left + distanceX >= positioningAreaBounds.left
    ) {
      xResult = calculateAxisCorrections(xOffsets, correctionDistance, axis.X, targetElementBounds);
    }
  }

  if (lockedAxis !== axis.X) {
    if (
      initialElementBounds.bottom + distanceY <= positioningAreaBounds.bottom &&
      initialElementBounds.top + distanceY >= positioningAreaBounds.top
    ) {
      yResult = calculateAxisCorrections(yOffsets, correctionDistance, axis.Y, targetElementBounds);
    }
  }

  // Collect meta information about applied corrections
  const appliedCorrections = [];

  if (xResult?.meta) {
    appliedCorrections.push(xResult.meta);
  }
  if (yResult?.meta) {
    appliedCorrections.push(yResult.meta);
  }

  return {
    x: xResult?.correction || 0,
    y: yResult?.correction || 0,
    targetElement: element,
    meta: {
      appliedCorrections,
      hasCorrection: appliedCorrections.length > 0,
      correctionDistance,
    },
  };
}

const computePositionCorrection = ({
  elements,
  initialElementBounds,
  positioningAreaBounds,
  distance,
  correctionDistance = 5,
  lockedAxis = null,
}) => {
  const corrections = elements.map(({ elementBounds: targetElementBounds, element }) =>
    calculateCorrection({
      initialElementBounds,
      targetElementBounds,
      positioningAreaBounds,
      distance,
      element,
      correctionDistance,
      lockedAxis,
    })
  );

  const result = corrections.reduce(
    (acc, correction) => {
      // Process X-axis corrections
      if (correction.x !== 0) {
        const absX = Math.abs(correction.x);

        if (acc.bestX === 0 || absX < acc.bestAbsX) {
          const xCorrection = correction.meta?.appliedCorrections?.find((c) => c.axis === axis.X);

          acc.bestX = correction.x;
          acc.bestAbsX = absX;
          acc.xCorrectionMeta = xCorrection
            ? {
                ...xCorrection,
                value: correction.x,
                targetElement: correction.targetElement,
              }
            : null;
        }
      }

      // Process Y-axis corrections
      if (correction.y !== 0) {
        const absY = Math.abs(correction.y);

        if (acc.bestY === 0 || absY < acc.bestAbsY) {
          const yCorrection = correction.meta?.appliedCorrections?.find((c) => c.axis === axis.Y);

          acc.bestY = correction.y;
          acc.bestAbsY = absY;
          acc.yCorrectionMeta = yCorrection
            ? {
                ...yCorrection,
                value: correction.y,
                targetElement: correction.targetElement,
              }
            : null;
        }
      }

      return acc;
    },
    {
      bestX: 0,
      bestY: 0,
      bestAbsX: Infinity,
      bestAbsY: Infinity,
      xCorrectionMeta: null,
      yCorrectionMeta: null,
    }
  );

  const appliedCorrections = [];

  if (result.xCorrectionMeta) {
    appliedCorrections.push(result.xCorrectionMeta);
  }

  if (result.yCorrectionMeta) {
    appliedCorrections.push(result.yCorrectionMeta);
  }

  return {
    x: result.bestX,
    y: result.bestY,
    meta: {
      appliedCorrections,
      hasCorrection: appliedCorrections.length > 0,
      totalCorrections: corrections.length,
    },
  };
};

export default computePositionCorrection;
